//#region IMPORT
import React, { useState, useEffect, useCallback } from 'react';
/** Services Import */
import Commons from '../../../../Services/Common/Common';
import Global from '../../../../Services/Common/Global';
/** Components */
import SAP_BASIC_01 from './SAP_BASIC_01';
import DialogBasic from '../../../../Components/MUIs/DialogBasic';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { closeLoading, showLoading } from '../../../../Reducer/LoadingReducer';
import useActionLog from '../../../../hooks/useActionLog';
import {
  useSearch1,
  useSearch2,
  useSearch3,
  useSave1,
} from './SAP_BASIC_01_QUERY';
import { useSave } from '../../../../hooks/useSaveN';
import { SAP_BASIC_01_Schema } from './SAP_BASIC_01_Schema';
//#endregion

// 화면 MENU_ID
const SCREEN_MENU_ID = 'SafetyPersonnel';

function SAP_BASIC_01F(props) {
  //#region 변수
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const searchInfo = useSelector((state) => state.SearchInfo);

  // 로그 관련
  const { SaveActionLog } = useActionLog();

  // 부서 목록
  const [departmentList, setDepartmentList] = useState([]);

  // 인력 목록
  const [memberList, setMemberList] = useState([]);

  // 조직도 초기화 확인 다이얼로그
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

  // 부서 목록에 추가 (수기 입력 포함)
  const handleDepartmentListAdd = (department) => {
    setDepartmentList((prev) => {
      // 중복 체크 (DEP_CD 또는 name 기준)
      const isDuplicate = prev.some(
        (dept) =>
          (dept.depCd && dept.depCd === department.depCd) ||
          (dept.depNm && dept.depNm === department.depNm) ||
          (dept.name && dept.name === department.name)
      );

      if (isDuplicate) {
        dispatch(
          showSnackbar({
            message: '이미 존재하는 부서입니다.',
            type: 'warning',
          })
        );
        return prev;
      }

      return [...prev, department];
    });
  };

  // 인력 목록에 추가 (수기 입력 포함)
  const handleMemberListAdd = (member) => {
    setMemberList((prev) => {
      // 중복 체크 (ID 또는 NAME 기준)
      const isDuplicate = prev.some(
        (mem) =>
          (mem.id && mem.id === member.id) ||
          (mem.name && mem.name === member.name && mem.id === member.id)
      );

      if (isDuplicate) {
        dispatch(
          showSnackbar({
            message: '이미 존재하는 인력입니다.',
            type: 'warning',
          })
        );
        return prev;
      }

      return [...prev, member];
    });
  };

  // 부서 목록에서 삭제 (수기 입력 항목만)
  const handleDepartmentListDelete = useCallback(
    (departmentId) => {
      setDepartmentList((prev) => {
        const department = prev.find(
          (dept) => (dept.depCd || dept.id) === departmentId
        );

        if (department && department.isManual === 'Y') {
          return prev.filter(
            (dept) => (dept.depCd || dept.id) !== departmentId
          );
        } else {
          dispatch(
            showSnackbar({
              message: '조회된 부서는 삭제할 수 없습니다.',
              type: 'warning',
            })
          );
          return prev;
        }
      });
    },
    [dispatch]
  );

  // 인력 목록에서 삭제 (수기 입력 항목만)
  const handleMemberListDelete = useCallback(
    (memberId) => {
      setMemberList((prev) => {
        const member = prev.find((mem) => (mem.id || mem.id) === memberId);

        if (member && member.isManual === 'Y') {
          return prev.filter((mem) => (mem.id || mem.id) !== memberId);
        } else {
          dispatch(
            showSnackbar({
              message: '조회된 인력은 삭제할 수 없습니다.',
              type: 'warning',
            })
          );
          return prev;
        }
      });
    },
    [dispatch]
  );

  // 조직도 데이터 구조
  const [organizationData, setOrganizationData] = useState({
    departments: [], // [{ id, DEP_CD, DEP_NM_LAST, x, y, ... }, ...]
    members: [], // [{ id, departmentId, ID, NAME, POSITION, ... }, ...]
    lines: [], // [{ id, type, x1, y1, x2, y2 }, ...] - 자동 생성된 선들
  });
  //#endregion

  //#region  조회
  const { run: Search1, runAsync: Search1Async } = useSearch1({
    onSuccess: (rows) => {
      console.log('getSapBasic01R3 조회결과값', rows);
      setDepartmentList((prev) =>
        Array.isArray(rows) && rows.length > 0 ? rows : prev
      );
    },
  });
  const { refetch: Search2 } = useSearch2({
    onSuccess: async (rows) => {
      const activeMembers = rows.filter((item) => item.useYn === 'Y');
      setMemberList(activeMembers);
      await Search1({ search: searchInfo });
      // 부서 및 인력 목록 조회 완료 후 조직도 데이터 조회
      if (props.validateSelection && props.validateSelection()) {
        await Search3();
      }
    },
  });
  const { run: Search3 } = useSearch3({
    onSuccess: (rows) => {
      console.log('rows', rows);
      // 조직도 데이터 조회 성공 처리
      if (!rows || rows.length === 0) {
        // 저장된 조직도가 없으면 초기화
        setOrganizationData({
          departments: [],
          members: [],
          lines: [],
        });
        return;
      }
      const data = rows[0];

      // 에러 체크
      if (data?.type === 'Error') {
        dispatch(
          showSnackbar({
            message: data.message || '조직도 조회 중 오류가 발생했습니다.',
            type: 'error',
          })
        );
        setOrganizationData({
          departments: [],
          members: [],
          lines: [],
        });
        return;
      }

      try {
        // 백엔드/버전별로 orgData / oraData 키가 혼재할 수 있어 둘 다 지원
        // - 신규 응답: { type: 'Success', orgData: { departments, members, lines }, ... }
        // - 기존 응답: { oraData: '{...json...}', ... }
        const orgDataRaw =
          data?.orgData ??
          data?.oraData ??
          data?.ORG_DATA ??
          data?.ORA_DATA ??
          null;

        // orgData가 암호화/문자열인 경우 복호화 + JSON 파싱
        let parsedOrgData = {};
        if (typeof orgDataRaw === 'string') {
          let orgDataJson = orgDataRaw;
          try {
            orgDataJson = Commons.DecryptAES(orgDataJson) || orgDataJson;
          } catch (decryptError) {}
          parsedOrgData = JSON.parse(orgDataJson || '{}');
        } else if (orgDataRaw && typeof orgDataRaw === 'object') {
          parsedOrgData = orgDataRaw;
        }

        // 공통 정규화 유틸: dept 접두어 제거 (dept- / dept_)
        const stripDeptPrefix = (value) => {
          if (value == null) return value;
          return String(value).replace(/^dept[-_]/, '');
        };

        // 화면 렌더링/저장이 기대하는 형태로 최소 정규화 (id=depCd / depNmLast / x,y 등)
        const normalizeDepartments = (departments = []) =>
          (departments || []).map((dept) => {
            const isManual = dept?.isManual === 'Y';
            const depCd = String(dept?.depCd ?? dept?.DEP_CD ?? '').trim();
            const nameParts = [
              dept?.depNm1,
              dept?.depNm2,
              dept?.depNm3,
              dept?.depNm4,
            ]
              .filter(Boolean)
              .join(' ');
            const depNmLast =
              dept?.depNmLast ??
              dept?.DEP_NM_LAST ??
              dept?.name ??
              nameParts ??
              '';

            return {
              ...dept,
              id:
                depCd ||
                stripDeptPrefix(dept?.id ?? dept?.ID) ||
                `dept_${Date.now()}_${Math.random()}`,
              depCd,
              depNmLast,
              name: dept?.name ?? depNmLast,
              // Search3 쿼리 케이스 대비 (X/Y, POS_X/POS_Y 등도 허용)
              x: Number.isFinite(dept?.x)
                ? dept.x
                : Number.isFinite(dept?.X)
                  ? dept.X
                  : Number.isFinite(dept?.POS_X)
                    ? dept.POS_X
                    : 0,
              y: Number.isFinite(dept?.y)
                ? dept.y
                : Number.isFinite(dept?.Y)
                  ? dept.Y
                  : Number.isFinite(dept?.POS_Y)
                    ? dept.POS_Y
                    : 0,
            };
          });

        const normalizedDepartments = normalizeDepartments(
          parsedOrgData?.departments || []
        );

        // members 정규화: (Name/Position/DepartmentId/IsManual 등 키 변형을 모두 흡수)
        // - 백엔드 변경: orgData.members 한 배열에 (수기/기존) 구성원을 함께 내려주고 isManual='Y'/'N'로 구분
        const normalizeMembers = (members = []) =>
          (members || []).map((member) => {
            const deptIdRaw =
              member?.departmentId ??
              member?.DepartmentId ??
              member?.DEP_CD ??
              member?.depCd ??
              null;
            const departmentId = stripDeptPrefix(deptIdRaw);

            const name =
              member?.name ??
              member?.NAME ??
              member?.Name ??
              member?.memberName ??
              member?.MEMBER_NAME ??
              '';
            const position =
              member?.position ??
              member?.POSITION ??
              member?.Position ??
              member?.memberPosition ??
              member?.MEMBER_POSITION ??
              '';

            // 사용자ID (수기=없음 / 일반=memberId)
            const memberId =
              member?.isManual === 'Y'
                ? null
                : (member?.memberId ??
                  member?.MemberId ??
                  member?.MEMBER_ID ??
                  member?.ID ??
                  null);

            return {
              ...member,
              // 화면 컴포넌트가 NAME / POSITION을 우선 참조하는 부분이 있어 둘 다 채움
              name,
              NAME: name,
              position,
              isManual: member.isManual,
              POSITION: position,
              departmentId,
              memberId,
              // 서버에서 내려주는 id(member_175 등)는 그대로 사용 (삭제/이동 안정성)
              id:
                member?.id ??
                member?.Id ??
                `member_${Date.now()}_${Math.random()}`,
            };
          });

        const rawMembersFromApi = parsedOrgData?.members || [];
        // 하위호환: 예전 응답(또는 일부 API)은 manualMembers를 별도 필드로 내려줄 수 있음
        const rawManualMembersFromApi =
          parsedOrgData?.manualMembers || data?.manualMembers || [];

        // 백엔드 변경으로 members에 수기/일반이 함께 올 수 있으므로
        // manualMembers까지 합쳐도 중복이 생기지 않도록 키 기반으로 dedupe
        const mergedMembers = [
          ...rawMembersFromApi,
          ...rawManualMembersFromApi,
        ];
        const normalizedMembers = (() => {
          const list = normalizeMembers(mergedMembers);
          const seen = new Set();
          const deduped = [];
          for (const m of list) {
            const deptKey = m.departmentId ?? '';
            const key =
              m.isManual === 'Y'
                ? `M|${deptKey}|${m.name || ''}|${m.position || ''}`
                : `R|${m.memberId || ''}`;
            if (seen.has(key)) continue;
            seen.add(key);
            deduped.push(m);
          }
          return deduped;
        })();

        // lines 정규화: source/target의 dept 접두어 제거 + 키 변형 흡수
        const normalizeLines = (lines = []) =>
          (lines || []).map((line) => {
            const sourceIdRaw =
              line?.sourceId ??
              line?.SourceId ??
              line?.source ??
              line?.Source ??
              null;
            const targetIdRaw =
              line?.targetId ??
              line?.TargetId ??
              line?.target ??
              line?.Target ??
              null;
            const sourceId = stripDeptPrefix(sourceIdRaw);
            const targetId = stripDeptPrefix(targetIdRaw);
            return {
              ...line,
              id: line?.id ?? line?.Id ?? `line_${Date.now()}_${Math.random()}`,
              sourceId,
              targetId,
              source: sourceId,
              target: targetId,
              startAnchor:
                line?.startAnchor ??
                line?.StartAnchor ??
                line?.START_ANCHOR ??
                'auto',
              endAnchor:
                line?.endAnchor ??
                line?.EndAnchor ??
                line?.END_ANCHOR ??
                'auto',
              path: line?.path ?? line?.Path ?? line?.PATH_TYPE ?? 'straight',
              curveness:
                line?.curveness ?? line?.Curveness ?? line?.CURVENESS ?? 0.5,
              strokeWidth:
                line?.strokeWidth ??
                line?.StrokeWidth ??
                line?.STROKE_WIDTH ??
                2,
              strokeColor:
                line?.strokeColor ??
                line?.StrokeColor ??
                line?.STROKE_COLOR ??
                '#333',
            };
          });
        setOrganizationData({
          departments: normalizedDepartments,
          members: normalizedMembers,
          lines: normalizeLines(parsedOrgData?.lines || []),
        });

        // 캔버스에 배치된 부서들을 사이드바 목록에서 제거
        if (normalizedDepartments.length > 0) {
          setDepartmentList((prev) => {
            const placedDepCds = new Set(
              normalizedDepartments
                .map((dept) => dept.depCd || dept.DEP_CD)
                .filter(Boolean)
            );

            return prev.filter(
              (dept) => !placedDepCds.has(dept.depCd || dept.DEP_CD)
            );
          });
        }

        // 수기 구성원을 왼쪽 목록(memberList)에도 추가
        // normalizedMembers 중 수기 구성원만 필터링하여 memberList에 병합
        // const manualMembersForList = normalizedMembers
        //   .filter((m) => m.isManual === true || m.IsManual === 'Y')
        //   .map((m) => {
        //     // memberList 형식에 맞게 변환 (일반 구성원과 동일한 구조)
        //     return {
        //       id: m.id || `manual_${Date.now()}_${Math.random()}`,
        //       ID: m.memberId || null, // 수기 구성원은 null
        //       name: m.name || m.NAME || '',
        //       NAME: m.name || m.NAME || '',
        //       position: m.position || m.POSITION || '',
        //       POSITION: m.position || m.POSITION || '',
        //       isManual: true,
        //       IsManual: 'Y',
        //       // memberList에서 사용할 수 있는 추가 필드
        //       depNm: m.departmentId
        //         ? normalizedDepartments.find((d) => d.id === m.departmentId)
        //             ?.depNmLast || ''
        //         : '',
        //     };
        //   });

        // // memberList에 수기 구성원 추가 (중복 체크)
        // if (manualMembersForList.length > 0) {
        //   setMemberList((prev) => {
        //     // 기존 목록과 병합 (중복 제거)
        //     const existingIds = new Set(
        //       prev.map((m) => {
        //         // 수기 구성원은 name + position으로 중복 체크
        //         if (m.isManual || m.IsManual === 'Y') {
        //           return `manual_${m.name}_${m.position || ''}`;
        //         }
        //         // 일반 구성원은 ID로 중복 체크
        //         return m.ID || m.id;
        //       })
        //     );

        //     const newManualMembers = manualMembersForList.filter((m) => {
        //       const key = `manual_${m.name}_${m.position || ''}`;
        //       return !existingIds.has(key);
        //     });

        //     return [...prev, ...newManualMembers];
        //   });
        // }
      } catch (parseError) {
        console.error('조직도 데이터 파싱 오류:', parseError);
        dispatch(
          showSnackbar({
            message: '조직도 데이터 파싱 중 오류가 발생했습니다.',
            type: 'error',
          })
        );
        setOrganizationData({
          departments: [],
          members: [],
          lines: [],
        });
      }
    },
  });
  const createSaveRequest = () => {
    // 저장 직전: 프론트에서 생성한 임시 id(dept_...)를 DB의 DEP_CD로 정규화
    const rawDepartments = organizationData.departments || [];
    console.log('rawDepartments', rawDepartments);
    const rawMembers = organizationData.members || [];
    console.log('rawMembers', rawMembers);
    const rawLines = organizationData.lines || [];
    // id -> depCd 매핑 테이블 생성
    const idToDepCd = new Map();
    rawDepartments.forEach((dept) => {
      const depCd = dept?.depCd || dept?.DEP_CD || null;
      const id = dept?.id ?? dept?.ID ?? null;
      if (depCd) {
        if (id != null) idToDepCd.set(String(id), depCd);
        idToDepCd.set(`dept-${depCd}`, depCd);
        idToDepCd.set(`dept_${depCd}`, depCd);
        idToDepCd.set(String(depCd), depCd);
        if (id != null) idToDepCd.set(`dept-${String(id)}`, depCd);
        if (id != null) idToDepCd.set(`dept_${String(id)}`, depCd);
      }
    });

    const normalizeDeptId = (maybeId) => {
      if (maybeId == null) return null;
      const key = String(maybeId);
      const stripped = key.replace(/^dept[-_]/, '');
      return (
        idToDepCd.get(key) ||
        idToDepCd.get(stripped) ||
        idToDepCd.get(`dept-${stripped}`) ||
        idToDepCd.get(`dept_${stripped}`) ||
        null
      );
    };
    // 부서: id를 depCd로 고정 (재조회/저장/선 연결 안정화)
    const departmentsToSave = rawDepartments.map((dept) => {
      const depCd = dept?.depCd || dept?.DEP_CD || null;
      if (!depCd) return dept;
      return { ...dept, id: depCd, depCd };
    });
    // 부서: 일반 부서와 수기 입력 부서 분리
    const regularDepartments = departmentsToSave.filter(
      (d) => d.isManual !== 'Y'
    );
    const manualDepartments = departmentsToSave
      .filter((d) => d.isManual === 'Y')
      .map((dept) => {
        return {
          depCd: dept.depCd,
          depNmLast: dept.depNmLast || '',
          x: dept.x || 0,
          y: dept.y || 0,
          zIndex: 0 || '',
        };
      });
    // 구성원: departmentId도 depCd로 정규화 + memberId(사용자ID) 확정
    const membersToSave = rawMembers
      .map((member) => {
        const deptId = member?.departmentId;
        const normalizedDeptCd = normalizeDeptId(deptId) || deptId;
        // 사용자ID 확정 (수기 입력이 아닌 경우만)
        const memberId =
          member?.isManual === 'Y'
            ? null // 수기 입력 구성원은 MEMBER_ID를 NULL로 설정
            : (member?.memberId ?? member?.ID ?? member?.id ?? null);

        // 수기 입력 구성원의 이름과 직책
        const memberName =
          member?.isManual === 'Y' ? member?.name || member?.NAME || '' : null;
        const memberPosition =
          member?.isManual === 'Y'
            ? member?.position || member?.POSITION || ''
            : null;

        return {
          ...member,
          departmentId: normalizedDeptCd, // (= DEP_CD로 쓰일 값)
          memberId, // 수기 입력이면 NULL, 아니면 사용자ID
          memberName: member?.isManual === 'Y' ? memberName : undefined, // 수기 입력용 이름
          memberPosition: member?.isManual === 'Y' ? memberPosition : undefined, // 수기 입력용 직책
          isManual: member?.isManual,
        };
      })
      .filter((m) => {
        // 수기 입력 구성원은 이름이 있으면 저장 가능
        if (m.isManual === 'Y') {
          return Boolean(m.memberName && m.memberName.trim());
        }
        // 일반 구성원은 memberId가 있어야 저장 가능
        return Boolean(m.memberId);
      });
    // 일반 구성원과 수기 입력 구성원 분리
    const regularMembers = membersToSave.filter((m) => m.isManual !== 'Y');
    const manualMembers = membersToSave
      .filter((m) => m.isManual === 'Y')
      .map((m) => {
        // departmentId는 이미 normalizedDeptCd로 변환되었지만,
        // 한번 더 정규화하여 확실히 DEP_CD로 변환
        const deptId = m.departmentId;
        const depCd = normalizeDeptId(deptId) || deptId;
        // depCd가 없거나 유효하지 않으면 저장하지 않음
        if (!depCd || depCd.trim() === '') {
          console.warn('수기 입력 구성원의 부서코드가 없습니다:', {
            member: m,
            departmentId: deptId,
            normalizedDepCd: depCd,
          });
          return null;
        }

        // 조직도에 배치된 부서의 실제 DEP_CD로 저장
        return {
          depCd: depCd, // ★ 조직도에 배치된 부서의 실제 DEP_CD
          memberName: m.memberName || '',
          memberPosition: m.memberPosition || null,
        };
      })
      .filter((m) => m !== null); // null 제거

    // 선: sourceId/targetId를 depCd로 정규화
    const linesToSave = rawLines
      .map((line) => {
        const sourceId = line?.sourceId ?? line?.source ?? null;
        const targetId = line?.targetId ?? line?.target ?? null;
        const sourceDepCd = normalizeDeptId(sourceId) || sourceId;
        const targetDepCd = normalizeDeptId(targetId) || targetId;
        return {
          ...line,
          sourceId: sourceDepCd,
          targetId: targetDepCd,
        };
      })
      // FK/DB 저장 관점에서 source/target이 없는 선은 제외
      .filter((line) => Boolean(line.sourceId) && Boolean(line.targetId));

    // 조직도 데이터를 JSON으로 직렬화 (구성원 분리)
    const orgDataToSave = {
      departments: regularDepartments, // 일반 부서만 (TB_SAP_ORG_DEPARTMENT_POS용)
      manualDepartments: manualDepartments, // 수기 입력 부서만 (TB_SAP_ORG_MANUAL_DEPARTMENT용)
      members: regularMembers, // 일반 구성원만 (TB_SAP_ORG_MEMBER_ASSIGN용)
      manualMembers: manualMembers, // 수기 입력 구성원만 (TB_SAP_ORG_MANUAL_MEMBER용)
      lines: linesToSave,
    };
    const orgDataJson = JSON.stringify(orgDataToSave);
    // 백엔드 API 형식에 맞춰 요청 객체 생성
    const saveRequest = {
      oraData: orgDataJson || '',
      orgImage: '' || '', // 이미지 경로는 별도 처리 필요 시 수정
    };

    return [saveRequest];
  };
  const { runAsync: Save1Async } = useSave1();
  const { run: Save1 } = useSave({
    menuId: SCREEN_MENU_ID,
    requiredLabel: '사업장명',
    schema: SAP_BASIC_01_Schema,
    getChangedRows: () => {
      const saveRequests = createSaveRequest();
      // 각 요청을 JSON 직렬화하여 배열로 반환
      return saveRequests;
    },
    getDeletedRows: () => [],

    requestSave: (rows) => Save1Async(rows),

    refresh: () => Search2(),
  });
  //#endregion

  //#region 조직도 데이터 관리
  // 부서 추가 (사이드바에서 캔버스로 드래그)
  const handleDepartmentAdd = (department) => {
    // 캔버스에 부서 추가
    setOrganizationData((prev) => {
      // 이미 같은 부서가 캔버스에 있는지 확인 (depCd 기준)
      const depCd = department.depCd || department.DEP_CD;
      const isAlreadyPlaced = prev.departments.some(
        (dept) => (dept.depCd || dept.DEP_CD) === depCd
      );

      if (isAlreadyPlaced) {
        dispatch(
          showSnackbar({
            message: '이미 캔버스에 배치된 부서입니다.',
            type: 'warning',
          })
        );
        return prev;
      }

      return {
        ...prev,
        departments: [...prev.departments, department],
      };
    });

    // 사이드바 목록에서 제거 (depCd 기준으로 찾아서 제거)
    setDepartmentList((prev) => {
      const depCd = department.depCd || department.DEP_CD;
      return prev.filter((dept) => (dept.depCd || dept.DEP_CD) !== depCd);
    });
  };

  // 부서 이동
  const handleDepartmentMove = (departmentId, x, y) => {
    setOrganizationData((prev) => ({
      ...prev,
      departments: prev.departments.map((dept) =>
        dept.id === departmentId ? { ...dept, x, y } : dept
      ),
    }));
  };

  // 부서 삭제 (캔버스에서 삭제 시 사이드바로 복구)
  const handleDepartmentDelete = (departmentId) => {
    setOrganizationData((prev) => {
      // 삭제할 부서 찾기
      const departmentToDelete = prev.departments.find(
        (dept) => dept.id === departmentId
      );

      if (!departmentToDelete) {
        return prev;
      }

      // 캔버스에서 부서 제거 및 관련 인력, 연결된 선 제거
      const updatedData = {
        ...prev,
        departments: prev.departments.filter(
          (dept) => dept.id !== departmentId
        ),
        members: prev.members.filter(
          (member) => member.departmentId !== departmentId
        ),
        // 해당 부서와 연결된 모든 선 제거
        lines: (prev.lines || []).filter((line) => {
          const sourceId = line.sourceId || line.source;
          const targetId = line.targetId || line.target;

          // sourceId나 targetId가 삭제되는 부서 id와 일치하는 선은 제거
          // dept- 접두사가 붙은 형식도 확인
          const sourceIdStr = String(sourceId || '');
          const targetIdStr = String(targetId || '');
          const departmentIdStr = String(departmentId || '');

          // 정확히 일치하거나 dept- 접두사가 붙은 경우 제거
          if (
            sourceId === departmentId ||
            targetId === departmentId ||
            sourceIdStr === `dept-${departmentIdStr}` ||
            targetIdStr === `dept-${departmentIdStr}` ||
            sourceIdStr === departmentIdStr ||
            targetIdStr === departmentIdStr
          ) {
            return false;
          }

          return true;
        }),
      };

      // 사이드바 목록에 다시 추가 (원본 부서 정보 복구)
      // 캔버스에 추가될 때 추가된 id, x, y 등의 속성 제거하고 원본 정보만 사용
      const depCd = departmentToDelete.depCd || departmentToDelete.DEP_CD;

      // 이미 사이드바에 있는지 확인
      setDepartmentList((currentList) => {
        const isAlreadyInList = currentList.some(
          (dept) => (dept.depCd || dept.DEP_CD) === depCd
        );

        if (isAlreadyInList) {
          return currentList;
        }

        // 원본 부서 정보 복구 (캔버스 전용 속성 제거)
        const {
          id: _id,
          x: _x,
          y: _y,
          ...originalDepartment
        } = departmentToDelete;

        return [...currentList, originalDepartment];
      });

      return updatedData;
    });
  };

  // 인력을 부서에 할당
  const handleMemberAssignToDepartment = (member, departmentId) => {
    // 사용자의 실제 ID를 먼저 확정해서 보관 (수기 입력이 아닌 경우만)
    const userMemberId =
      member?.isManual === 'Y' ? null : (member?.ID ?? member?.id ?? null);
    // 이미 다른 부서에 할당된 경우 제거
    // 수기 입력 구성원은 이름으로, 일반 구성원은 memberId로 구분
    const existingMember = organizationData.members.find((m) => {
      if (member?.isManual === 'Y') {
        // 수기 입력 구성원은 이름과 직책으로 중복 체크
        const memberName = member?.name || member?.NAME || '';
        const memberPosition = member?.position || member?.POSITION || '';
        return (
          member?.isManual === 'Y' &&
          (m?.name || m?.NAME) === memberName &&
          (m?.position || m?.POSITION) === memberPosition
        );
      } else {
        // 일반 구성원은 memberId로 중복 체크
        const existingMemberId = m?.memberId ?? m?.ID ?? m?.id;
        return existingMemberId === userMemberId;
      }
    });

    if (existingMember) {
      // 같은 부서에 이미 할당된 경우 중복 방지
      if (existingMember.departmentId === departmentId) {
        dispatch(
          showSnackbar({
            message: '이미 해당 부서에 할당된 인력입니다.',
            type: 'warning',
          })
        );
        return;
      }
      // 다른 부서에서 제거
      setOrganizationData((prev) => ({
        ...prev,
        members: prev.members.filter((m) => {
          if (member?.isManual === 'Y') {
            // 수기 입력 구성원은 이름과 직책으로 구분
            const memberName = member?.name || member?.NAME || '';
            const memberPosition = member?.position || '';
            return !(
              member?.isManual === 'Y' &&
              (m?.name || m?.NAME) === memberName &&
              (m?.position || m?.POSITION) === memberPosition
            );
          } else {
            // 일반 구성원은 memberId로 구분
            const existingMemberId = m?.memberId ?? m?.ID ?? m?.id;
            return existingMemberId !== userMemberId;
          }
        }),
      }));
    }

    // 새로 할당
    setOrganizationData((prev) => ({
      ...prev,
      members: [
        ...prev.members,
        {
          ...member,
          // 화면 컴포넌트가 NAME / POSITION을 우선 참조하는 부분이 있어 둘 다 채움
          name: member.name || member.NAME || '',
          NAME: member.NAME || member.name || '',
          position: member.position || member.POSITION || '',
          POSITION: member.POSITION || member.position || '',
          // ★ 사용자ID는 memberId로 별도 보존 (수기 입력이면 NULL)
          memberId: userMemberId,
          // 화면용/리스트용 임시 키는 따로 유지
          id: `member_${Date.now()}_${Math.random()}`,
          departmentId,
          // 수기 입력 여부 보존
          isManual: member?.isManual,
        },
      ],
    }));
  };

  // 인력 제거
  const handleMemberRemove = (memberId) => {
    setOrganizationData((prev) => ({
      ...prev,
      members: prev.members.filter((m) => m.id !== memberId),
    }));
  };

  // 선 추가
  const handleLineAdd = (line) => {
    setOrganizationData((prev) => ({
      ...prev,
      lines: [...(prev.lines || []), line],
    }));
  };

  // 선 삭제
  const handleLineDelete = (lineId) => {
    setOrganizationData((prev) => ({
      ...prev,
      lines: (prev.lines || []).filter((line) => line.id !== lineId),
    }));
  };

  // 선 위치 업데이트 (선 그리기 모드에서 이동 시)
  const handleLineUpdate = (lineId, updates) => {
    setOrganizationData((prev) => ({
      ...prev,
      lines: (prev.lines || []).map((line) =>
        line.id === lineId ? { ...line, ...updates } : line
      ),
    }));
  };

  // 조직도 초기화
  const handleClear = () => {
    setClearConfirmOpen(true);
  };

  const handleClearConfirm = () => {
    setClearConfirmOpen(false);

    // 캔버스에 있던 부서들을 사이드바로 복구하기 위해 부서 목록 재조회
    Search1Async({ search: searchInfo }).then(() => {
      setOrganizationData({
        departments: [],
        members: [],
        lines: [],
      });
      dispatch(
        showSnackbar({
          message: '조직도가 초기화되었습니다.',
          type: 'info',
        })
      );
    });
  };
  //#endregion

  return (
    <div className="wrap">
      <SAP_BASIC_01
        departmentList={departmentList}
        memberList={memberList}
        organizationData={organizationData}
        onDepartmentAdd={handleDepartmentAdd}
        onDepartmentMove={handleDepartmentMove}
        onDepartmentDelete={handleDepartmentDelete}
        onDepartmentListAdd={handleDepartmentListAdd}
        onDepartmentListDelete={handleDepartmentListDelete}
        onMemberAssignToDepartment={handleMemberAssignToDepartment}
        onMemberRemove={handleMemberRemove}
        onMemberListAdd={handleMemberListAdd}
        onMemberListDelete={handleMemberListDelete}
        onLineAdd={handleLineAdd}
        onLineDelete={handleLineDelete}
        onLineUpdate={handleLineUpdate}
        onClear={handleClear}
        Save1={Save1}
        saveClick1={props.saveClick1}
        setSaveClick1={props.setSaveClick1}
        onGenerateImage={async (imageDataUrl) => {
          // 이미지를 파일로 변환하여 저장
          try {
            const response = await fetch(imageDataUrl);
            const blob = await response.blob();
            const file = new File(
              [blob],
              `조직도_${new Date().toISOString().replace(/T/, '').replace(/\..+/, '').replace(/-/g, '').replace(/:/g, '')}.png`,
              { type: 'image/png' }
            );

            // 파일 업로드
            const filePath = `SafetyPersonnel/${props.searchRef.current.custCd}/SAP_BASIC_01`;
            const formData = new FormData();
            formData.append('UserId', userInfo.userid);
            formData.append('FilePath', filePath);
            formData.append('FormFile', file);

            const uploadResult = await Commons.Axios(
              'file2',
              Global.file.useUrl + Global.urls.Execute.videoUpload,
              null,
              formData
            );

            const imagePath = uploadResult?.data?.filePath || '';
            if (imagePath) {
              const cleanedPath = imagePath
                .replace(/^.*\\SafetyPersonnel/, 'SafetyPersonnel')
                .replace(/\\/g, '/');
              return cleanedPath;
            }
            return null;
          } catch (error) {
            console.error('이미지 저장 오류:', error);
            return null;
          }
        }}
        validateSelection={props.validateSelection}
        searchRef={props.searchRef}
      />

      {/* 조직도 초기화 확인 다이얼로그 */}
      <DialogBasic
        open={clearConfirmOpen}
        onClose={() => setClearConfirmOpen(false)}
        title="조직도 초기화"
        contentText="조직도를 초기화하시겠습니까? 모든 데이터가 삭제됩니다."
        useSaveButton={true}
        useCloseButton={true}
        saveButtonName="확인"
        closeButtonName="취소"
        onSave={handleClearConfirm}
      />
    </div>
  );
}

export default SAP_BASIC_01F;
